import React, { useState } from 'react';
import { Tabs, Tab, Box, Stack, Chip, IconButton, Tooltip } from '@mui/material';
import { tabsClasses } from '@mui/material/Tabs';
import { SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import MainCard from './MainCard';

const TabPanel = ({
    title,
    tabs,
    defaultTab,
    actionComponent: ActionComponent,
    actionComponentActions,
    onTabChange,
    headerComponent,
    filterToggle
}) => {
    const tabKeys = Object.keys(tabs);
    const [activeTab, setActiveTab] = useState(defaultTab || tabKeys[0]);

    const handleChange = (_, newValue) => {
        setActiveTab(newValue);
        if (onTabChange) {
            onTabChange(newValue);
        }
    };

    const getActions = activeTab => {
        if (!actionComponentActions) return null;

        const actions = actionComponentActions(activeTab);
        if (!actions) return null;

        return Array.isArray(actions) ? actions : [actions];
    };

    const actions = getActions(activeTab);
    const toggle = filterToggle ? filterToggle(activeTab) : null;

    return (
        <MainCard content={false} title={title}>
            <Box
                sx={{
                    p: 2,
                    pr: 3,
                    pb: 0,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2
                }}
            >
                {headerComponent && <Box sx={{ flexShrink: 0 }}>{headerComponent}</Box>}
                <Tabs
                    value={activeTab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        flexGrow: 1,
                        [`& .${tabsClasses.scrollButtons}`]: {
                            '&.Mui-disabled': { opacity: 0.3 }
                        }
                    }}
                >
                    {tabKeys.map(key => (
                        <Tab
                            key={key}
                            label={tabs[key].label}
                            value={key}
                            icon={
                                <Chip
                                    label={tabs[key].count || 0}
                                    color={key === activeTab ? 'primary' : 'secondary'}
                                    variant="light"
                                    size="small"
                                />
                            }
                            iconPosition="end"
                        />
                    ))}
                </Tabs>

                {((ActionComponent && actions) || toggle) && (
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            flexGrow: 0,
                            flexShrink: 0,
                            ml: 2,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        {toggle && (
                            <Tooltip title={toggle.active ? 'Hide filters' : 'Search & filter'}>
                                <IconButton
                                    onClick={toggle.onClick}
                                    size="small"
                                    sx={{
                                        border: '1px solid',
                                        borderRadius: 1,
                                        borderColor: toggle.active
                                            ? 'primary.main'
                                            : 'grey.300',
                                        color: toggle.active
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        bgcolor: toggle.active
                                            ? 'primary.lighter'
                                            : 'transparent'
                                    }}
                                >
                                    <SearchOutlined />
                                </IconButton>
                            </Tooltip>
                        )}
                        {ActionComponent &&
                            actions &&
                            actions.map((action, index) => (
                                <ActionComponent key={index} action={action} />
                            ))}
                    </Stack>
                )}
            </Box>
            {tabKeys.map(key => (
                <Box
                    key={key}
                    hidden={key !== activeTab}
                    role="tabpanel"
                    id={`tabpanel-${key}`}
                    aria-labelledby={`tab-${key}`}
                >
                    {key === activeTab && tabs[key].content}
                </Box>
            ))}
        </MainCard>
    );
};

TabPanel.propTypes = {
    title: PropTypes.node,
    tabs: PropTypes.objectOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            content: PropTypes.node.isRequired,
            count: PropTypes.number
        })
    ).isRequired,
    defaultTab: PropTypes.string,
    actionComponent: PropTypes.elementType,
    actionComponentActions: PropTypes.func,
    onTabChange: PropTypes.func,
    filterToggle: PropTypes.func
};

export default TabPanel;
